// The action list at the top of Authorizations: what needs requesting today, in
// the advisor's order, and why.
//
// The alerts strip below it says what is wrong; this says what to do about it —
// renewal or more units, the reasons ("expires in 6d", "PT 18/20 used"), the
// caveats that travel with the flag ("confirm the patient isn't being discharged
// before renewing"), and the payer's channel. A flag is a suggestion for a
// person: nothing here requests an authorization, and case 2 ("running out") is
// explicitly more art than science, which is why its caveats are never hidden.
//
// Authorizations already requested are not in the ranked list; they sit in
// "Waiting on payer" with how long they have waited. A denied or more-info answer
// comes back to the top with that as its reason — the server decides that, this
// only renders it.
//
// Request ↗ opens the payer's portal or Availity in a new tab, or shows the
// email / fax / phone contact; Copy packet puts the request's details on the
// clipboard. Both leave the asking to a person — Atlas never contacts a payer.

import React, { useEffect, useRef, useState } from "react";

import {
  ACTION_LABELS,
  CHANNEL_LABELS,
  KIND_LABELS,
  TIMING_LABELS,
  fetchAdvisor,
  safeHref,
} from "./api";
import MarkRequestedModal from "./MarkRequestedModal";
import OutcomeModal from "./OutcomeModal";
import PacketModal from "./PacketModal";

export function RouteChip({ route }) {
  if (!route) {
    return (
      <span
        className="auth-route-chip auth-route-chip-unknown"
        title="No request route on file for this payer — add one on Practice Details"
      >
        No route on file
      </span>
    );
  }
  const timing = TIMING_LABELS[route.timing];
  return (
    <span className={`auth-route-chip auth-route-chip-${route.channel}`}>
      {CHANNEL_LABELS[route.channel] || route.channel}
      {timing ? ` · ${timing}` : ""}
    </span>
  );
}

const OUTCOME_LABELS = {
  denied: "Denied",
  more_info: "Payer asked for more info",
};

// Where to ask. A portal or 278 target becomes a new-tab link only when it is a
// real http(s) URL; anything else (David's seed has "(B2C portal)") is shown as
// text, never put in an href. Email, fax and phone are shown, not linked: a
// mailto would start an email to a payer from this page, and Atlas does not.
export function RequestLink({ route }) {
  if (!route || route.channel === "none") return null;
  const label = CHANNEL_LABELS[route.channel] || route.channel;
  if (route.channel === "portal" || route.channel === "edi_278") {
    const href = safeHref(route.target);
    if (href) {
      return (
        <a
          className="auth-btn auth-btn-link auth-request-link"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          title={`Open ${href} in a new tab`}
        >
          Request ↗
        </a>
      );
    }
    // The chip beside this already names the channel, so only the target.
    return (
      <span className="auth-route-contact">
        {route.target || `no ${label.toLowerCase()} address on file`}
      </span>
    );
  }
  return (
    <span className="auth-route-contact">
      {route.target || `no ${label.toLowerCase()} contact on file`}
    </span>
  );
}

// `canRequest` (biller and above, like working the queue) decides Mark requested
// and Copy packet; `onCreate` is passed only to those who may add an
// authorization (admin).
function FlagRow({ flag, canRequest, onMark, onCreate, onPacket }) {
  // By auth id, not by action: a denied initial request on an existing auth comes
  // back as `request_initial` WITH an auth id, and needs Mark requested again,
  // not a second authorization row.
  const initial = flag.authId == null;
  return (
    <li className="auth-flag">
      <div className="auth-flag-head">
        <span className={`auth-flag-action auth-flag-action-${flag.action}`}>
          {ACTION_LABELS[flag.action] || "Review"}
        </span>
        <span className="auth-alert-who">
          {flag.patientName || "Unmatched patient"}
        </span>
        <span className="auth-alert-sep">·</span>
        <span>{flag.authority || flag.payerName || "No payer"}</span>
        {flag.authNumber && (
          <>
            <span className="auth-alert-sep">·</span>
            <span className="auth-alert-number">{flag.authNumber}</span>
          </>
        )}
        {flag.kinds.includes("possibly_exhausted") && (
          <span
            className="auth-flag-kind auth-flag-kind-possibly_exhausted"
            title="Counted visits plus visits no code assigned to a discipline would reach the cap"
          >
            May be exhausted
          </span>
        )}
        {flag.outcome && OUTCOME_LABELS[flag.outcome] && (
          <span className="auth-flag-outcome">
            {OUTCOME_LABELS[flag.outcome]}
            {flag.outcomeNote ? `: ${flag.outcomeNote}` : ""}
          </span>
        )}
        <RouteChip route={flag.route} />
        <span className="auth-flag-actions">
          <RequestLink route={flag.route} />
          {canRequest && flag.packetAvailable && flag.authId != null && (
            <button
              type="button"
              className="auth-btn auth-btn-link"
              onClick={() => onPacket(flag)}
            >
              Copy packet
            </button>
          )}
          {initial
            ? onCreate && (
                <button
                  type="button"
                  className="auth-btn auth-btn-link"
                  onClick={() => onCreate(flag.subject)}
                >
                  Add authorization
                </button>
              )
            : canRequest &&
              // An action we cannot read is not offered a write.
              flag.action !== "unknown" && (
                <button
                  type="button"
                  className="auth-btn auth-btn-link"
                  onClick={() => onMark(flag)}
                >
                  Mark requested
                </button>
              )}
        </span>
      </div>
      {flag.reasons.length > 0 && (
        <ul className="auth-flag-reasons">
          {flag.reasons.map((r, i) => (
            <li key={i}>{r}</li>
          ))}
        </ul>
      )}
      {flag.caveats.length > 0 && (
        <div className="auth-flag-caveats">
          <span className="auth-flag-caveats-label">Before requesting</span>
          <ul>
            {flag.caveats.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

// Auths a route's conditions kept off the list (e.g. "auth only from the 4th
// episode"), with why — so a missing renewal is a decision you can see, not a gap.
function SuppressedGroup({ suppressed }) {
  const [open, setOpen] = useState(false);
  if (suppressed.length === 0) return null;
  return (
    <div className="auth-waiting">
      <button
        type="button"
        className="auth-waiting-toggle"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        {open ? "▾" : "▸"} Not flagged by a route condition ({suppressed.length})
      </button>
      {open && (
        <ul className="auth-alert-list">
          {suppressed.map((x, i) => (
            <li key={x.key || `s-${i}`} className="auth-alert-row">
              <span className="auth-alert-who">{x.authority || "Unknown payer"}</span>
              {x.wouldHave.length > 0 && (
                <span>would have: {x.wouldHave.join("; ")}</span>
              )}
              <span className="auth-alert-why">{x.reasons.join("; ")}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function WaitingGroup({ waiting, canRequest, onOutcome }) {
  const [open, setOpen] = useState(false);
  if (waiting.length === 0) return null;
  return (
    <div className="auth-waiting">
      <button
        type="button"
        className="auth-waiting-toggle"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        {open ? "▾" : "▸"} Waiting on payer ({waiting.length})
      </button>
      {open && (
        <ul className="auth-alert-list">
          {waiting.map((w, i) => (
            <li key={w.authId ?? `w-${i}`} className="auth-alert-row">
              <span className="auth-alert-who">
                {w.patientName || "Unmatched patient"}
              </span>
              <span className="auth-alert-sep">·</span>
              <span>{w.authority || w.payerName || "No payer"}</span>
              {w.authNumber && (
                <>
                  <span className="auth-alert-sep">·</span>
                  <span className="auth-alert-number">{w.authNumber}</span>
                </>
              )}
              {canRequest && w.authId != null && (
                <button
                  type="button"
                  className="auth-btn auth-btn-link auth-waiting-action"
                  onClick={() => onOutcome(w)}
                >
                  Record answer
                </button>
              )}
              <span className="auth-alert-why">
                {w.kind ? `${KIND_LABELS[w.kind] || w.kind} requested` : "Requested"}
                {w.channel ? ` by ${(CHANNEL_LABELS[w.channel] || w.channel).toLowerCase()}` : ""}
                {w.daysPending == null
                  ? ""
                  : w.daysPending === 0
                    ? " today"
                    : ` ${w.daysPending} day${w.daysPending === 1 ? "" : "s"} ago`}
                {w.ref ? ` · ref ${w.ref}` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ActionList({
  practiceId,
  generation,
  canRequest,
  onCreate,
  onChanged,
  // A write elsewhere made the page's data stale (a 409): the table reloads too.
  onStale,
}) {
  const [state, setState] = useState({
    loading: true,
    failed: null,
    flags: [],
    waiting: [],
    suppressed: [],
    truncated: false,
    scheduleAvailable: null,
  });
  const [marking, setMarking] = useState(null);
  const [packetFor, setPacketFor] = useState(null);
  const [answering, setAnswering] = useState(null);
  // Reloads this list alone, for a write elsewhere made it stale (a 409).
  const [staleTick, setStaleTick] = useState(0);
  // Only the newest request may write. The parent keys this component by
  // practice, so a practice switch remounts it; the sequence covers two reloads
  // in flight within one practice (a save, then another).
  const seq = useRef(0);

  useEffect(() => {
    if (!practiceId) return undefined;
    const mine = ++seq.current;
    setState((s) => ({ ...s, loading: true }));
    fetchAdvisor(practiceId)
      .then((data) => {
        if (seq.current !== mine) return;
        setState({ loading: false, failed: null, ...data });
      })
      .catch((err) => {
        if (seq.current !== mine) return;
        setState({
          loading: false,
          failed:
            err.message || "Could not check which authorizations need requesting.",
          flags: [],
          waiting: [],
          suppressed: [],
          truncated: false,
          scheduleAvailable: null,
        });
      });
    return undefined;
  }, [practiceId, generation, staleTick]);

  const { loading, failed, flags, waiting, suppressed, truncated, scheduleAvailable } =
    state;

  let body;
  if (loading && flags.length === 0 && waiting.length === 0) {
    body = <p className="auth-alerts-quiet">Checking what needs requesting…</p>;
  } else if (failed) {
    // Never the all-clear. "Nothing to request" is a claim that a renewal will
    // not be missed; a failed check cannot make it.
    body = (
      <p className="auth-actions-failed">
        <strong>Could not check what needs requesting.</strong> {failed} Treat
        this as unknown rather than clear.
      </p>
    );
  } else {
    body = (
      <>
        {truncated && (
          <p className="auth-actions-note auth-actions-truncated">
            The missing-authorization scan stopped at its row limit, so this list
            may be incomplete.
          </p>
        )}
        {flags.length === 0 && !truncated ? (
          <p className="auth-alerts-quiet">
            Nothing to request today — no authorization is expiring, running
            out, or missing a request.
          </p>
        ) : flags.length === 0 ? null : (
          <ol className="auth-flag-list">
            {flags.map((flag, i) => (
              <FlagRow
                key={flag.key || `f-${i}`}
                flag={flag}
                canRequest={canRequest}
                onMark={setMarking}
                onCreate={onCreate}
                onPacket={setPacketFor}
              />
            ))}
          </ol>
        )}
        <WaitingGroup
          waiting={waiting}
          canRequest={canRequest}
          onOutcome={setAnswering}
        />
        <SuppressedGroup suppressed={suppressed} />
      </>
    );
  }

  return (
    <section className="auth-actions" aria-label="To request">
      <div className="auth-actions-head">
        <h2 className="auth-actions-title">
          To request{!loading && !failed ? ` (${flags.length})` : ""}
        </h2>
        {scheduleAvailable === false && !failed && (
          <span className="auth-actions-note">
            Scheduled visits are not counted — this practice has no schedule
            feed, so “running out” reads completed visits only.
          </span>
        )}
      </div>
      {body}
      {marking && (
        <MarkRequestedModal
          practiceId={practiceId}
          flag={marking}
          onClose={() => setMarking(null)}
          onStale={() => {
            setStaleTick((n) => n + 1);
            if (onStale) onStale();
          }}
          onDone={(text) => {
            setMarking(null);
            onChanged(text);
          }}
        />
      )}
      {packetFor && (
        <PacketModal
          practiceId={practiceId}
          flag={packetFor}
          onClose={() => setPacketFor(null)}
        />
      )}
      {answering && (
        <OutcomeModal
          practiceId={practiceId}
          subject={answering}
          onClose={() => setAnswering(null)}
          onStale={() => {
            setStaleTick((n) => n + 1);
            if (onStale) onStale();
          }}
          onDone={(text) => {
            setAnswering(null);
            onChanged(text);
          }}
        />
      )}
    </section>
  );
}
